import { captureException } from "@sentry/react";
import { getCsrf } from "./csrf";
import { getToken } from "./security";
import { createNetworkErrorCause } from "./sentry";

export namespace Fetch {
  interface Base {
    isAuthorized: boolean;
    isSubscribed: boolean;
    loginUrl?: string;
  }

  export interface Success<T> extends Base {
    status: "success";
    message?: null;
    errors?: null;
    data: T;
  }

  export interface Fail<Fields extends string = never> extends Base {
    status: "error";
    message: string;
    errors?: Errors<Fields> | null;
    data?: null;
  }

  export interface YiiFail {
    name: string;
    message?: string;
    code: number;
    status: number;
  }

  export type Errors<Fields extends string = never> = Partial<
    Record<ErrorKey<Fields>, string>
  >;

  export type ErrorKey<Fields extends string> =
    | `${Fields}[${number}]`
    | `${Fields}`;

  export type Return<T> = {
    data: T;
    response: Response;
  };
}

export class Network {
  static async fetch<T, ErrorKey extends string = string>(
    input: RequestInfo | URL,
    init?: RequestInit,
  ): Promise<Fetch.Return<T>> {
    try {
      const token = await getToken();

      const headers = new Headers(init?.headers);

      headers.set("X-Client-Token", btoa(JSON.stringify(token.payload)));
      headers.set("X-Client-Signature", token.signature);

      const response = await fetch(input, {
        redirect: "follow",
        ...init,
        headers,
      });

      const data: Fetch.Success<T> | Fetch.Fail<ErrorKey> | Fetch.YiiFail =
        await response.json();

      if (data.status === "success") {
        return {
          data: data.data,
          response,
        };
      }

      if (data.status === "error") {
        if (data.loginUrl !== undefined) {
          window.location.assign(data.loginUrl);
        }

        throw new NetworkError(data.message, {
          errors: data.errors ?? undefined,
        });
      }

      throw new FormatError(data.message ?? "Response format error", {
        cause: new Error(
          createNetworkErrorCause({
            message: "Unknown response",
            input,
            init,
            data,
          }),
        ),
      });
    } catch (error) {
      if (error instanceof NetworkError) {
        throw error;
      }

      if (error instanceof Error) {
        captureException(
          new Error(error.message, {
            cause: new Error(
              createNetworkErrorCause({
                message: error.name,
                input,
                init,
              }),
            ),
          }),
        );
      } else {
        captureException(error);
      }

      throw new NetworkError((error as Error).message, {
        cause: new Error(
          createNetworkErrorCause({
            message: (error as Error).name,
            input,
            init,
          }),
          {
            cause: (error as Error).cause,
          },
        ),
      });
    }
  }

  static async get<T, K extends object = object>(
    input: URL,
    data?: K,
  ): Promise<Fetch.Return<T>>;
  static async get<T, K extends object = object>(
    input: string,
    data?: K,
  ): Promise<Fetch.Return<T>>;
  static async get<T>(input: Request): Promise<Fetch.Return<T>>;
  static async get<T, K extends object = object>(
    input: RequestInfo | URL,
    data?: K,
  ): Promise<Fetch.Return<T>> {
    if (data !== undefined) {
      if (typeof input === "string") {
        return Network.fetch<T>(Network.url(input, data));
      }

      if (input instanceof URL) {
        return Network.fetch<T>(Network.url(input.toString(), data));
      }
    }

    return Network.fetch<T>(input);
  }

  static async post<T>(input: RequestInfo | URL): Promise<Fetch.Return<T>>;
  static async post<T>(
    input: RequestInfo | URL,
    data: FormData,
  ): Promise<Fetch.Return<T>>;
  static async post<T, K = unknown>(
    input: RequestInfo | URL,
    data: K,
  ): Promise<Fetch.Return<T>>;
  static async post<T, K = unknown>(
    input: RequestInfo | URL,
    data?: K | FormData,
  ): Promise<Fetch.Return<T>> {
    if (data instanceof FormData) {
      return Network.fetch<T>(input, {
        method: "POST",
        body: data,
      });
    }

    const headers: HeadersInit = {
      "Content-Type": "application/json",
      "X-CSRF-Token": getCsrf(),
    };

    return Network.fetch<T>(input, {
      method: "POST",
      body: JSON.stringify(data),
      headers,
    });
  }

  static async delete<T>(input: RequestInfo | URL): Promise<Fetch.Return<T>>;
  static async delete<T>(
    input: RequestInfo | URL,
    data: FormData,
  ): Promise<Fetch.Return<T>>;
  static async delete<T, K = unknown>(
    input: RequestInfo | URL,
    data: K,
  ): Promise<Fetch.Return<T>>;
  static async delete<T, K = unknown>(
    input: RequestInfo | URL,
    data?: K | FormData,
  ): Promise<Fetch.Return<T>> {
    if (data instanceof FormData) {
      return Network.fetch<T>(input, {
        method: "DELETE",
        body: data,
      });
    }

    const headers: HeadersInit = {
      "Content-Type": "application/json",
      "X-CSRF-Token": getCsrf(),
    };

    return Network.fetch<T>(input, {
      method: "DELETE",
      body: JSON.stringify(data),
      headers,
    });
  }

  static async upload<T>(input: RequestInfo | URL, data: FormData) {
    return Network.fetch<T>(input, {
      method: "POST",
      body: data,
    });
  }

  static url<T extends object = object>(uri: string, data?: T): URL {
    const url = new URL(uri, document.location.origin);

    if (data !== undefined) {
      for (const [key, value] of Object.entries(data)) {
        if (value !== undefined && value !== null) {
          url.searchParams.append(key, value.toString());
        }
      }
    }

    return url;
  }
}

interface NetworkErrorOptions<
  ErrorKey extends string = string,
> extends ErrorOptions {
  errors?: Fetch.Errors<ErrorKey>;
}

export class NetworkError<ErrorKey extends string = string> extends Error {
  override name = "NetworkError";

  public readonly errors: Map<string, Error>;

  constructor(message: string, options: NetworkErrorOptions<ErrorKey> = {}) {
    super(message, {
      cause: options.cause,
    });

    this.errors = new Map(
      Object.entries(options.errors ?? {}).map((entry) => {
        const [key, message] = entry as [Fetch.ErrorKey<ErrorKey>, string];

        return [key, new Error(message)];
      }),
    );
  }

  public getError = (key: ErrorKey, ...indices: number[]) => {
    const index = indices
      .map((index) => `[${index}]`)
      .join("") as `[${number}]`;

    return this.errors.get(`${key}${index}`) ?? this.errors.get(key);
  };
}

class FormatError extends NetworkError {
  override name = "FormatError";
}
